import * as React from "react";
import { Button } from "@fluentui/react-components";
import { CheckmarkCircleRegular, DeleteRegular, ArrowUndoRegular } from "@fluentui/react-icons";
import type { DokumentKommentar, KommentarFaden } from "../../../services/dokumentKommentarService";

/**
 * Die aufgeklappte Kommentarblase neben dem Chat-Symbol — verankert an der
 * Stelle, KEIN modaler Dialog (User, 28.09.2026). Getippt wird in einem
 * `contentEditable`-Kasten, nicht in `<input>`/`<textarea>`: Enter sendet,
 * Umschalt+Enter macht eine neue Zeile, Esc minimiert.
 */

/** „vor 5 Min.“ — grob genug für einen Faden, genau genug für „wer war zuletzt dran“. */
export function relativeZeit(iso: string | null, jetzt: number = Date.now()): string {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return "";
    const min = Math.floor((jetzt - t) / 60_000);
    if (min < 1) return "gerade eben";
    if (min < 60) return `vor ${min} Min.`;
    const std = Math.floor(min / 60);
    if (std < 24) return `vor ${std} Std.`;
    const tage = Math.floor(std / 24);
    if (tage < 7) return tage === 1 ? "vor 1 Tag" : `vor ${tage} Tagen`;
    return new Date(t).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const AUB = "#7d2169";

const stil = {
    blase: {
        position: "absolute",
        width: 300,
        maxHeight: 420,
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        border: "1px solid #d9cfd8",
        borderTop: `3px solid ${AUB}`,
        borderRadius: 6,
        boxShadow: "0 6px 20px rgba(40, 20, 40, 0.25)",
        pointerEvents: "auto",
        fontSize: 13,
        color: "#2b2230",
        cursor: "auto",
        userSelect: "text",
        zIndex: 2,
    } as React.CSSProperties,
    zitat: {
        margin: "8px 10px 0",
        padding: "2px 8px",
        borderLeft: "3px solid #f0c000",
        background: "#fffbe6",
        fontStyle: "italic",
        color: "#5b4f5e",
        fontSize: 12,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    } as React.CSSProperties,
    faden: { overflowY: "auto", padding: "6px 10px", display: "flex", flexDirection: "column", gap: 8 } as React.CSSProperties,
    kopf: { display: "flex", alignItems: "baseline", gap: 6 } as React.CSSProperties,
    autor: { fontWeight: 600 } as React.CSSProperties,
    zeit: { fontSize: 11, color: "#8a7d8c" } as React.CSSProperties,
    text: { whiteSpace: "pre-wrap", overflowWrap: "anywhere", marginTop: 2 } as React.CSSProperties,
    kastenRahmen: { position: "relative", margin: "0 10px 8px" } as React.CSSProperties,
    kasten: {
        minHeight: 34,
        maxHeight: 120,
        overflowY: "auto",
        padding: "6px 8px",
        border: "1px solid #cfc3cd",
        borderRadius: 4,
        outline: "none",
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        background: "#fcfafc",
    } as React.CSSProperties,
    platzhalter: {
        position: "absolute",
        left: 9,
        top: 7,
        color: "#9a8e9c",
        pointerEvents: "none",
    } as React.CSSProperties,
    leiste: {
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 6px 6px",
        borderTop: "1px solid #eee6ec",
    } as React.CSSProperties,
    bestaetigen: { fontSize: 12, color: "#8a1d1d", paddingLeft: 4 } as React.CSSProperties,
    erledigtMarke: { fontSize: 11, color: "#3b7a3b", marginLeft: "auto" } as React.CSSProperties,
};

/**
 * `plaintext-only` nimmt nur Text an (keine Formatierung per Strg+B, kein
 * eingefügtes HTML). Ältere Browser kennen den Wert nicht — dann `true`, und
 * Einfügen/Ablegen fangen `onPaste`/`onDrop` unten ab.
 */
let plaintextOnly: boolean | null = null;
function editierbarWert(): "plaintext-only" | "true" {
    if (plaintextOnly === null) {
        try {
            const probe = document.createElement("div");
            probe.setAttribute("contenteditable", "plaintext-only");
            plaintextOnly = probe.contentEditable === "plaintext-only";
        } catch {
            plaintextOnly = false;
        }
    }
    return plaintextOnly ? "plaintext-only" : "true";
}

/** Reinen Text an der Einfügemarke einsetzen — ohne `execCommand` (veraltet, in Tests nicht da). */
function textEinsetzen(el: HTMLElement, text: string): void {
    const sel = window.getSelection?.();
    if (sel && sel.rangeCount > 0 && el.contains(sel.getRangeAt(0).commonAncestorContainer)) {
        const r = sel.getRangeAt(0);
        r.deleteContents();
        const knoten = document.createTextNode(text);
        r.insertNode(knoten);
        r.setStartAfter(knoten);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
        return;
    }
    el.appendChild(document.createTextNode(text));
}

/** Kasten zum Tippen — `contentEditable`, kein Formularfeld. */
const TippKasten: React.FC<{
    label: string;
    platzhalter: string;
    autoFocus?: boolean;
    entwurf: React.MutableRefObject<string>;
    onSenden: (text: string) => Promise<boolean>;
    onSchliessen: () => void;
}> = ({ label, platzhalter, autoFocus, entwurf, onSenden, onSchliessen }) => {
    const ref = React.useRef<HTMLDivElement | null>(null);
    const [leer, setLeer] = React.useState(!entwurf.current);
    const [sendet, setSendet] = React.useState(false);

    React.useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.textContent = entwurf.current;
        // Nur beim Einhängen: danach gehört der Inhalt dem Nutzer.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Fokus erst im normalen Effekt: die Blase hängt per Portal in einem Host,
    // den die Ebene selbst erst in IHREM Layout-Effekt an die Seite hängt —
    // davor steht der Kasten nicht im Dokument und `focus()` liefe ins Leere.
    React.useEffect(() => {
        if (autoFocus) ref.current?.focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const lesen = () => {
        const el = ref.current;
        // innerText kennt die Zeilenumbrüche (<br>/<div>) — jsdom hat es nicht.
        return (el ? (el.innerText ?? el.textContent ?? "") : "").replace(/ /g, " ");
    };

    return (
        <div style={stil.kastenRahmen}>
            {leer && <span style={stil.platzhalter}>{platzhalter}</span>}
            <div
                ref={ref}
                role="textbox"
                aria-multiline="true"
                aria-label={label}
                // React kennt nur boolesche Werte für contentEditable — das Attribut direkt setzen.
                {...{ contenteditable: sendet ? "false" : editierbarWert() }}
                suppressContentEditableWarning
                style={stil.kasten}
                onInput={() => {
                    entwurf.current = lesen();
                    setLeer(!entwurf.current.trim());
                }}
                // Nur reiner Text: kein HTML, keine Bilder aus der Zwischenablage.
                onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData?.getData("text/plain") ?? "";
                    if (!text || !ref.current) return;
                    textEinsetzen(ref.current, text);
                    entwurf.current = lesen();
                    setLeer(!entwurf.current.trim());
                }}
                onDrop={(e) => e.preventDefault()}
                onDragOver={(e) => e.preventDefault()}
                onKeyDown={(e) => {
                    // PDF.js und die anderen Ebenen hören auf Tasten am Dokument.
                    e.stopPropagation();
                    if (e.key === "Escape") {
                        e.preventDefault();
                        onSchliessen();
                        return;
                    }
                    // Enter in einer IME-Komposition (z. B. Japanisch) bestätigt nur die Zeichen.
                    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                    if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        const text = lesen().trim();
                        if (!text || sendet) return;
                        setSendet(true);
                        void onSenden(text).then((ok) => {
                            setSendet(false);
                            if (!ok) return;
                            entwurf.current = "";
                            if (ref.current) ref.current.textContent = "";
                            setLeer(true);
                        });
                    }
                }}
            />
        </div>
    );
};

const Eintrag: React.FC<{
    k: DokumentKommentar;
    loeschbar: boolean;
    onLoeschenFragen: () => void;
}> = ({ k, loeschbar, onLoeschenFragen }) => (
    <div data-kommentar-eintrag={k.id}>
        <div style={stil.kopf}>
            <span style={stil.autor}>{k.autorName}</span>
            <span style={stil.zeit} title={k.erstellt ? new Date(k.erstellt).toLocaleString("de-DE") : undefined}>
                {relativeZeit(k.erstellt)}
            </span>
            {loeschbar && (
                <Button
                    appearance="transparent"
                    size="small"
                    icon={<DeleteRegular />}
                    aria-label="Antwort löschen"
                    title="Antwort löschen"
                    style={{ marginLeft: "auto", minWidth: 0 }}
                    onClick={onLoeschenFragen}
                />
            )}
        </div>
        <div style={stil.text}>{k.text}</div>
    </div>
);

export const KommentarBlase: React.FC<{
    /** `null` = neuer Kommentar (Entwurf): nur der Kasten. */
    faden: KommentarFaden | null;
    zitat: string;
    position: { left: number; top: number };
    darfSchreiben: boolean;
    /** Ungesendeter Text — überlebt das Minimieren. */
    entwurf: React.MutableRefObject<string>;
    onSenden: (text: string) => Promise<boolean>;
    onErledigt: (wert: boolean) => void;
    onLoeschen: (id: number) => void;
    onSchliessen: () => void;
}> = ({ faden, zitat, position, darfSchreiben, entwurf, onSenden, onErledigt, onLoeschen, onSchliessen }) => {
    // Inline-Bestätigung statt `window.confirm`: welcher Eintrag gelöscht werden soll.
    const [loeschFrage, setLoeschFrage] = React.useState<number | null>(null);
    const fadenEnde = React.useRef<HTMLDivElement | null>(null);
    const anzahl = faden ? faden.antworten.length : 0;
    React.useEffect(() => {
        fadenEnde.current?.scrollIntoView?.({ block: "nearest" });
    }, [anzahl]);

    return (
        <div
            data-kommentar-blase={faden ? faden.id : "entwurf"}
            role="group"
            aria-label={faden ? `Kommentar von ${faden.autorName}` : "Neuer Kommentar"}
            style={{ ...stil.blase, left: position.left, top: position.top }}
            // Klicks und Tasten in der Blase gehören der Blase — nicht PDF.js,
            // nicht dem Aufziehen der Werkzeuge.
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") onSchliessen();
            }}
        >
            {zitat && <div style={stil.zitat} title={zitat}>„{zitat}“</div>}
            {faden && (
                <div style={stil.faden}>
                    <Eintrag k={faden} loeschbar={false} onLoeschenFragen={() => undefined} />
                    {faden.antworten.map((a) => (
                        <Eintrag
                            key={a.id}
                            k={a}
                            loeschbar={a.darfLoeschen}
                            onLoeschenFragen={() => setLoeschFrage(a.id)}
                        />
                    ))}
                    <div ref={fadenEnde} />
                </div>
            )}
            {darfSchreiben && (
                <TippKasten
                    label={faden ? "Antwort schreiben" : "Kommentar schreiben"}
                    platzhalter={faden ? "Antworten … (Enter sendet)" : "Kommentar … (Enter speichert)"}
                    autoFocus={!faden}
                    entwurf={entwurf}
                    onSenden={onSenden}
                    onSchliessen={onSchliessen}
                />
            )}
            {faden && (darfSchreiben || faden.darfLoeschen) && (
                <div style={stil.leiste}>
                    {loeschFrage !== null ? (
                        <>
                            <span style={stil.bestaetigen}>
                                {loeschFrage === faden.id ? "Ganzen Faden löschen?" : "Antwort löschen?"}
                            </span>
                            <Button
                                size="small"
                                appearance="primary"
                                onClick={() => {
                                    const id = loeschFrage;
                                    setLoeschFrage(null);
                                    onLoeschen(id);
                                }}
                            >
                                Ja, löschen
                            </Button>
                            <Button size="small" appearance="subtle" onClick={() => setLoeschFrage(null)}>
                                Abbrechen
                            </Button>
                        </>
                    ) : (
                        <>
                            {darfSchreiben && (
                                <Button
                                    size="small"
                                    appearance="subtle"
                                    icon={faden.erledigt ? <ArrowUndoRegular /> : <CheckmarkCircleRegular />}
                                    onClick={() => onErledigt(!faden.erledigt)}
                                    title={faden.erledigt ? "Faden wieder öffnen" : "Faden als erledigt markieren"}
                                >
                                    {faden.erledigt ? "Wieder öffnen" : "Erledigt"}
                                </Button>
                            )}
                            {faden.darfLoeschen && (
                                <Button
                                    size="small"
                                    appearance="subtle"
                                    icon={<DeleteRegular />}
                                    onClick={() => setLoeschFrage(faden.id)}
                                    title="Kommentar mit allen Antworten löschen"
                                >
                                    Löschen
                                </Button>
                            )}
                            {faden.erledigt && <span style={stil.erledigtMarke}>erledigt</span>}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
